import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../../../lib/api";
import { PageHeader } from "../components/shared/PageHeader";
import { AdminTable } from "../components/shared/AdminTable";
import { SlideOver } from "../components/shared/SlideOver";
import { ShieldPlus } from "lucide-react";
import { useForm } from "react-hook-form";
import { useAdminT } from "../../../hooks/useAdminT";

interface Role {
    id: string;
    name: string;
    description?: string;
    permissions: string[];
}

export default function RolesPage() {
    const { t } = useAdminT();
    const queryClient = useQueryClient();
    const [isDrawOpen, setIsDrawOpen] = useState(false);
    const [editingId, setEditingId] = useState<string | null>(null);

    const { register, handleSubmit, reset, setValue } = useForm();

    const { data: roles, isLoading } = useQuery({
        queryKey: ["roles"],
        queryFn: () => api.get("/api/admin/roles").then(res => res.data.data)
    });

    const { data: permissions } = useQuery({
        queryKey: ["permissions"],
        queryFn: () => api.get("/api/admin/permissions").then(res => res.data.data)
    });

    const createMutation = useMutation({
        mutationFn: (data: any) => api.post("/api/admin/roles", data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["roles"] });
            handleClose();
        }
    });

    const updateMutation = useMutation({
        mutationFn: (data: any) => api.put(`/api/admin/roles/${editingId}`, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["roles"] });
            handleClose();
        }
    });

    const deleteMutation = useMutation({
        mutationFn: (id: string) => api.delete(`/api/admin/roles/${id}`),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ["roles"] })
    });

    const handleClose = () => {
        setIsDrawOpen(false);
        setEditingId(null);
        reset();
    };

    const handleEdit = (role: Role) => {
        setEditingId(role.id);
        reset({ name: role.name, description: role.description });
        // Set permissions (assuming checkbox array)
        setValue("permissions", role.permissions);
        setIsDrawOpen(true);
    };

    const onSubmit = (data: any) => {
        if (editingId) {
            updateMutation.mutate(data);
        } else {
            createMutation.mutate(data);
        }
    };

    return (
        <div className="p-8">
            <PageHeader
                title={t("roles.title")}
                description={t("roles.subtitle")}
                actions={[
                    {
                        label: t("roles.add"),
                        icon: ShieldPlus,
                        onClick: () => setIsDrawOpen(true)
                    }
                ]}
            />

            <AdminTable
                data={roles}
                isLoading={isLoading}
                columns={[
                    { header: t("roles.fields.name"), accessorKey: "name", className: "font-medium capitalize" },
                    { header: t("common.description"), accessorKey: "description" },
                    {
                        header: t("roles.fields.permissions"),
                        cell: (r) => (
                            <div className="flex flex-wrap gap-1">
                                {r.permissions.slice(0, 3).map((p: string) => (
                                    <span key={p} className="text-xs bg-slate-100 px-1.5 py-0.5 rounded border border-slate-200">
                                        {p}
                                    </span>
                                ))}
                                {r.permissions.length > 3 && (
                                    <span className="text-xs text-slate-500 pl-1">{t("common.more", { count: r.permissions.length - 3 })}</span>
                                )}
                            </div>
                        )
                    },
                ]}
                onEdit={handleEdit}
                onDelete={(r) => {
                    if (confirm(t("common.confirmProceed"))) deleteMutation.mutate(r.id);
                }}
            />

            <SlideOver
                isOpen={isDrawOpen}
                onClose={handleClose}
                title={editingId ? t("roles.edit") : t("roles.add")}
                footer={
                    <>
                        <button onClick={handleClose} className="px-4 py-2 border rounded hover:bg-slate-50">{t("common.cancel")}</button>
                        <button onClick={handleSubmit(onSubmit)} className="px-4 py-2 bg-secondary text-white rounded hover:bg-primary-dark">{t("common.save")}</button>
                    </>
                }
            >
                <form className="space-y-6">
                    <div>
                        <label className="block text-sm font-medium">{t("roles.fields.name")}</label>
                        <input {...register("name", { required: true })} className="mt-1 w-full px-3 py-2 border rounded-md" placeholder={t("roles.placeholders.name")} />
                    </div>
                    <div>
                        <label className="block text-sm font-medium">{t("common.description")}</label>
                        <textarea {...register("description")} className="mt-1 w-full px-3 py-2 border rounded-md" rows={3} />
                    </div>

                    <div>
                        <label className="block text-sm font-medium mb-2">{t("roles.fields.permissions")}</label>
                        <div className="space-y-2 max-h-60 overflow-y-auto p-2 border rounded bg-slate-50">
                            {permissions?.map((p: any) => (
                                <div key={p.id} className="flex items-center">
                                    <input
                                        type="checkbox"
                                        id={p.id}
                                        value={p.name}
                                        {...register("permissions")}
                                        className="rounded border-slate-300 text-secondary focus:ring-primary"
                                    />
                                    <label htmlFor={p.id} className="ml-2 text-sm text-slate-700">
                                        {p.name} <span className="text-xs text-slate-400">({p.description})</span>
                                    </label>
                                </div>
                            ))}
                        </div>
                    </div>
                </form>
            </SlideOver>
        </div>
    );
}
